type CustomSelectInstance = {
    root: HTMLElement;
    button: HTMLButtonElement;
    list: HTMLElement;
    options: HTMLElement[];
    select: HTMLSelectElement;
    activeIndex: number;
    signature: string;
};

let activeInstance: CustomSelectInstance | null = null;
let instanceId = 0;
let globalListenersBound = false;

const SELECTOR = 'select:not([multiple]):not([data-native-select])';

const visibleOptions = (select: HTMLSelectElement): HTMLOptionElement[] =>
    Array.from(select.options).filter(option => !option.hidden);

const selectedOptionText = (select: HTMLSelectElement): string => {
    const selected = select.selectedOptions[0] ?? select.options[select.selectedIndex] ?? select.options[0];
    return selected?.textContent?.trim() ?? '';
};

const optionSignature = (select: HTMLSelectElement): string =>
    visibleOptions(select)
        .map(option => `${option.value}\u001f${option.textContent?.trim() ?? ''}\u001f${option.disabled ? '1' : '0'}`)
        .join('\u001e');

const ensureId = (element: HTMLElement, prefix: string): string => {
    if (!element.id) {
        element.id = `${prefix}-${++instanceId}`;
    }

    return element.id;
};

const findSelectLabel = (select: HTMLSelectElement): HTMLElement | null => {
    if (select.getAttribute('aria-labelledby')) {
        return null;
    }

    if (select.id) {
        const label = Array.from(document.querySelectorAll<HTMLLabelElement>('label[for]'))
            .find(candidate => candidate.htmlFor === select.id);
        if (label) return label;
    }

    const floatingField = select.closest<HTMLElement>('.float-label');
    if (!floatingField) return null;

    return Array.from(floatingField.children)
        .find((child): child is HTMLElement => child instanceof HTMLElement && child.tagName === 'SPAN') ?? null;
};

const setExpanded = (instance: CustomSelectInstance, expanded: boolean): void => {
    instance.root.classList.toggle('is-open', expanded);
    instance.button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    instance.list.hidden = !expanded;
};

const setActiveOption = (instance: CustomSelectInstance, index: number): void => {
    if (!instance.options.length) return;

    const nextIndex = Math.min(Math.max(index, 0), instance.options.length - 1);
    instance.activeIndex = nextIndex;

    instance.options.forEach((option, optionIndex) => {
        option.classList.toggle('is-active', optionIndex === nextIndex);
    });

    const active = instance.options[nextIndex];
    if (active) {
        active.scrollIntoView({ block: 'nearest' });
    }
};

const closeActive = (): void => {
    if (!activeInstance) return;

    setExpanded(activeInstance, false);
    activeInstance = null;
};

const openInstance = (instance: CustomSelectInstance): void => {
    if (activeInstance && activeInstance !== instance) {
        closeActive();
    }

    activeInstance = instance;
    setExpanded(instance, true);

    const selectedIndex = instance.options.findIndex(option => option.getAttribute('aria-selected') === 'true');
    setActiveOption(instance, selectedIndex >= 0 ? selectedIndex : 0);
};

const syncFromNative = (instance: CustomSelectInstance): void => {
    if (instance.signature !== optionSignature(instance.select)) {
        renderOptions(instance);
    }

    const value = instance.select.value;
    const valueLabel = instance.button.querySelector<HTMLElement>('[data-select-value]');

    if (valueLabel) {
        valueLabel.textContent = selectedOptionText(instance.select);
    }

    instance.options.forEach((option, optionIndex) => {
        const isSelected = option.dataset.value === value;
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        option.classList.toggle('is-selected', isSelected);

        if (isSelected) {
            instance.activeIndex = optionIndex;
        }
    });
};

const renderOptions = (instance: CustomSelectInstance): void => {
    instance.options = [];
    instance.list.replaceChildren();

    visibleOptions(instance.select).forEach(option => {
        const item = document.createElement('div');
        item.className = 'site-select__option';
        item.role = 'option';
        item.tabIndex = -1;
        item.dataset.value = option.value;
        item.textContent = option.textContent?.trim() ?? '';

        if (option.disabled) {
            item.setAttribute('aria-disabled', 'true');
        }

        item.addEventListener('click', () => selectOption(instance, item));
        item.addEventListener('mouseenter', () => setActiveOption(instance, instance.options.indexOf(item)));

        instance.options.push(item);
        instance.list.appendChild(item);
    });

    instance.signature = optionSignature(instance.select);
};

const selectOption = (instance: CustomSelectInstance, option: HTMLElement): void => {
    if (option.getAttribute('aria-disabled') === 'true') return;

    instance.select.value = option.dataset.value ?? '';
    syncFromNative(instance);
    closeActive();
    instance.button.focus();
    instance.select.dispatchEvent(new Event('change', { bubbles: true }));
};

const bindGlobalListeners = (): void => {
    if (globalListenersBound) return;
    globalListenersBound = true;

    document.addEventListener('pointerdown', event => {
        if (!activeInstance) return;
        const target = event.target as Node | null;
        if (target && activeInstance.root.contains(target)) return;
        closeActive();
    });

    window.addEventListener('resize', closeActive);
};

const enhanceSelect = (select: HTMLSelectElement): void => {
    if (select.dataset.customSelectReady === 'true') return;

    const options = visibleOptions(select);
    if (!options.length) return;

    select.dataset.customSelectReady = 'true';

    const id = ++instanceId;
    const label = findSelectLabel(select);
    const labelId = label ? ensureId(label, 'site-select-label') : select.getAttribute('aria-labelledby');
    const root = document.createElement('div');
    const button = document.createElement('button');
    const value = document.createElement('span');
    const chevron = document.createElement('span');
    const list = document.createElement('div');
    const valueId = `site-select-value-${id}`;
    const listId = `site-select-list-${id}`;

    root.className = 'site-select';
    if (select.closest('.float-select')) root.classList.add('site-select--float');
    if (select.closest('.site-field--select')) {
        root.classList.add('site-select--compact');
    }

    button.type = 'button';
    button.className = 'site-select__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', listId);

    value.className = 'site-select__value';
    value.id = valueId;
    value.dataset.selectValue = '';
    value.textContent = selectedOptionText(select);

    chevron.className = 'site-select__chevron';
    chevron.setAttribute('aria-hidden', 'true');

    button.append(value, chevron);

    if (labelId) {
        button.setAttribute('aria-labelledby', `${labelId} ${valueId}`);
    } else {
        button.setAttribute('aria-label', select.getAttribute('aria-label') ?? selectedOptionText(select));
    }

    list.className = 'site-select__menu';
    list.id = listId;
    list.role = 'listbox';
    list.hidden = true;

    const instance: CustomSelectInstance = {
        root,
        button,
        list,
        options: [],
        select,
        activeIndex: 0,
        signature: ''
    };

    renderOptions(instance);

    button.addEventListener('click', () => {
        if (activeInstance === instance) {
            closeActive();
            return;
        }

        openInstance(instance);
    });

    if (label) {
        label.addEventListener('click', event => {
            event.preventDefault();
            button.focus();
        });
    }

    button.addEventListener('keydown', event => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (activeInstance !== instance) {
                    openInstance(instance);
                } else {
                    setActiveOption(instance, instance.activeIndex + 1);
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (activeInstance !== instance) {
                    openInstance(instance);
                } else {
                    setActiveOption(instance, instance.activeIndex - 1);
                }
                break;
            case 'Home':
                if (activeInstance === instance) {
                    event.preventDefault();
                    setActiveOption(instance, 0);
                }
                break;
            case 'End':
                if (activeInstance === instance) {
                    event.preventDefault();
                    setActiveOption(instance, instance.options.length - 1);
                }
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                if (activeInstance !== instance) {
                    openInstance(instance);
                } else {
                    const option = instance.options[instance.activeIndex];
                    if (option) selectOption(instance, option);
                }
                break;
            case 'Escape':
                if (activeInstance === instance) {
                    event.preventDefault();
                    closeActive();
                }
                break;
            case 'Tab':
                closeActive();
                break;
        }
    });

    select.addEventListener('change', () => syncFromNative(instance));
    select.addEventListener('input', () => syncFromNative(instance));

    select.hidden = true;
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;
    select.closest('.float-select')?.classList.add('is-enhanced');

    root.append(button, list);
    select.insertAdjacentElement('afterend', root);
    syncFromNative(instance);

    const observer = new MutationObserver(() => syncFromNative(instance));
    observer.observe(select, {
        attributes: true,
        attributeFilter: ['disabled', 'hidden', 'label', 'value'],
        childList: true,
        subtree: true
    });
};

export const initCustomSelects = (): void => {
    bindGlobalListeners();
    document.querySelectorAll<HTMLSelectElement>(SELECTOR).forEach(enhanceSelect);
};
